<template>
  <span
    ref="el"
    class="ranking-option"
    :class="{
      'ranking-option--dropdown': isDropdown,
      'dropdown-expanded': isOpened,
      'absolute-dropdown': absoluteDropdown,
      selected,
      disabled,
    }"
    :style="{ ...(isDropdown ? { minWidth: `${maxWidth}px` } : {}) }"
  >
    <div class="selected-item" @click="clickItem(item)">
      <div>{{ isDropdown && selected ? modelValue?.name : item.name }}</div>
      <i v-if="isDropdown" class="dropdown-btn" />
    </div>
    <DropDownElement
      v-if="isDropdown"
      ref="elOptions"
      class="dropdown-options"
      :expanded="isOpened"
      :style="{ minWidth: `${maxWidth}px` }"
    >
      <div class="dropdown-options-inner">
        <li v-for="one in finalOptions" :key="one.key" @click="clickItem(one)">{{ one.name }}</li>
      </div>
    </DropDownElement>
  </span>
</template>
<script setup lang="ts">
  import { rankingOptionI } from '@/config/rankings'

  const isOpened = ref(false)

  interface Props {
    item: rankingOptionI
    modelValue: rankingOptionI | undefined
    filter?: string
    disabled?: boolean
    absoluteDropdown?: boolean
  }

  const props = withDefaults(defineProps<Props>(), {
    filter: '',
    disabled: false,
    absoluteDropdown: true,
  })

  const el = ref<HTMLDivElement>()
  const elOptions = ref()
  const isDropdown = computed(() => !!props.item.children)

  const finalOptions = computed<rankingOptionI[]>(() => {
    return props.item.children?.filter((x) => !x.filter || x.filter === props.filter) ?? []
  })
  const selected = computed(() =>
    props.disabled
      ? false
      : isDropdown.value
      ? !!finalOptions.value.find((x) => x.key === props.modelValue?.key)
      : props.item.key === props.modelValue?.key
  )

  watchEffect(() => {
    if (!props.disabled && props.modelValue?.kind === props.item.key && !selected.value) {
      setTimeout(() => {
        emits('update:modelValue', undefined)
      }, 0)
    }
  })

  const { width: rootWidth } = useElementSize(el)
  const { width: optionsWidth } = useElementSize(elOptions)

  const maxWidth = computed(() => Math.round(Math.max(optionsWidth.value, rootWidth.value)))

  if (isDropdown.value) {
    onClickOutside(el, () => (isOpened.value = false))
  }

  const emits = defineEmits(['update:modelValue'])

  function clickItem(item: rankingOptionI) {
    if (!isDropdown.value || item.key !== props.item.key) emits('update:modelValue', item)
    if (isDropdown.value) {
      isOpened.value = !isOpened.value
    }
  }

  watch(
    () => props.disabled,
    () => {
      if (props.disabled) isOpened.value = false
    }
  )
</script>
<style lang="scss" scoped>
  $transition-time: 0.2s;
  .dropdown-enter-active {
    transition: opacity $transition-time ease-out;
  }
  // .dropdown-leave-active {
  //   transition: opacity 0.1s ease-in;
  // }
  .dropdown-enter-from,
  .dropdown-leave-to {
    // transform: translateY(-100%);
    opacity: 0;
  }

  .dropdown-enter-from {
    transition: none;
  }

  .ranking-option {
    position: relative;
    display: inline-flex;
    flex-direction: column;
    align-items: center;
    font-family: 'Glegoo';
    font-style: normal;
    font-weight: 700;
    font-size: 17px;
    color: white;
    text-transform: uppercase;
    user-select: none;

    .selected-item {
      position: relative;
      width: 100%;
      display: inline-flex;
      align-items: center;
      // justify-content: space-between;
      // column-gap: 10px;
      cursor: pointer;
      // padding: 0 6px;
      > div {
        padding: 2px 12px;
        // padding: 0 6px;
        // width: calc(100% - 20px);
        flex-grow: 1;
        white-space: nowrap;
        text-align: center;
      }
    }

    &:hover {
      .selected-item,
      .dropdown-options-inner {
        background: var(--theme-blue-active-color);
        border-color: var(--theme-blue-color);
      }
      .dropdown-options-inner {
        color: #b3bfdb;
      }
    }

    // &--dropdown {
    //   .selected-item {
    //     padding-right: 22px; // for dropdown-btn
    //   }
    // }

    .selected-item,
    .dropdown-options-inner {
      border-radius: 8px;
      border: 1px solid;
      border-top: none;
      background: #b3bfdb;
      border-color: #9fadcd;
      transition: background-color 0.3s ease;
    }
    &.selected {
      .selected-item,
      .dropdown-options-inner {
        background: var(--theme-blue-active-color);
        border-color: var(--theme-blue-color);
      }
    }

    &.disabled {
      .selected-item {
        background: #d9d9d9;
        border-color: #d9d9d9;
        box-shadow: inset 0 2px 1.5px rgba(0, 0, 0, 0.05);
        pointer-events: none !important;
      }
    }
  }

  .dropdown-btn {
    // position: absolute;
    // right: 0;
    z-index: 3;
    background-image: url('@/assets/imgs/ranking/btn-dropdown.png');
    min-width: 22px;
    height: 21px;
    margin-right: 4px;
    transition: transform $transition-time ease-out;
  }

  .dropdown-expanded {
    z-index: 4;
    .dropdown-btn {
      transform: rotate(180deg);
    }
  }
  .absolute-dropdown {
    .dropdown-options {
      position: absolute;
      z-index: 2;
    }
  }
  .dropdown-options {
    // overflow: hidden;
    top: 100%;
    margin-top: -7px;
    // min-width: 100%;
    // padding: 4px 4px 4px 0;
    .dropdown-options-inner {
      width: 100%;
      padding: 4px;
      padding-top: 6px;
      border-radius: 0 0 8px 8px;
      cursor: pointer;
      display: inline-flex;
      flex-direction: column;
      align-items: center;
      padding-bottom: 8px;
      // max-height: 250px;
      overflow-y: auto;
      overflow-x: hidden;
      white-space: nowrap;
      text-align: center;
      > li {
        list-style: none;
        width: 100%;
        padding: 0 10px;
        &:hover {
          color: white;
        }
      }
    }

    ::-webkit-scrollbar {
      width: 6px;
    }

    /* Track */
    ::-webkit-scrollbar-track {
      background-color: #566ea4;
      border-radius: 5px;
    }

    /* Handle */
    ::-webkit-scrollbar-thumb {
      background: #bbd0ff;
      border-radius: 5px;
    }

    /* Handle on hover */
    ::-webkit-scrollbar-thumb:hover {
      background: #aac0df;
    }
  }
</style>
